import type { AppContext } from './types';

/**
 * Registre MUTABLE en mémoire (framework-agnostique) du contexte courant.
 *
 * Les composants publient leur état (utilisateur, route, contexte d'écran) via les
 * setters ; l'enveloppe LIT au clic (aucune persistance, aucune sérialisation
 * passive). Métadonnées uniquement.
 */

interface StaticInfo {
  app: string;
  version: string;
  buildId: string | null;
  hostname: string | null;
}

let staticInfo: StaticInfo = { app: 'xphoto', version: '0.0.0', buildId: null, hostname: null };
let user: { id: number | string; role: string } | null = null;
let route: string | null = null;
let appContext: AppContext = {};

/** Enregistre/complète les informations statiques de l'app (merge partiel). */
export function registerApp(info: Partial<StaticInfo>): void {
  staticInfo = { ...staticInfo, ...info };
}

/** Informations statiques courantes. */
export function getStaticInfo(): StaticInfo {
  return { ...staticInfo };
}

/** Publie l'utilisateur courant (id + rôle), ou `null` si anonyme. */
export function setUser(u: { id: number | string; role: string } | null): void {
  user = u ? { id: u.id, role: u.role } : null;
}

/** Utilisateur courant. */
export function getUser(): { id: number | string; role: string } | null {
  return user ? { ...user } : null;
}

/** Publie la route normalisée courante. */
export function setRoute(r: string | null): void {
  route = r;
}

/** Route courante. */
export function getRoute(): string | null {
  return route;
}

/** Fusionne un contexte d'écran partiel dans le contexte courant. */
export function setAppContext(ctx: Partial<AppContext>): void {
  appContext = { ...appContext, ...ctx };
}

/** Vide le contexte d'écran (au démontage d'un écran). */
export function clearAppContext(): void {
  appContext = {};
}

/** Snapshot du contexte d'écran (objet vide si rien de publié). */
export function collectAppContext(): AppContext | Record<string, never> {
  return { ...appContext };
}

/** Réinitialise utilisateur, route et contexte d'écran (garde les infos statiques). */
export function resetContext(): void {
  user = null;
  route = null;
  appContext = {};
}
